import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTransition, animated } from 'react-spring';
import useResizeListener from '../../../../hooks/useResizeListener';
import mediaQuery from '../../../../helpers/mediaQuery';
import getPressedKey, { KeyboardMap } from '../../../../helpers/getPressedKey';
import SearchButton from './components/searchButton/searchButton';
import Input from './components/input/input';
import CloseButton from './components/closeButton/closeButton';
import Dropdown from './components/dropdown/dropdown';
import Logo from './components/logo/logo';
import BackdropOverlay from '../../../../components/backdropOverlay/backdropOverlay';
import useFetch from './hooks/useFetch';
import useBodyScrollLock from '../../../../hooks/useBodyScrollLock';
import style from './searchApp.module.pcss';
import removeTrailingSpaces from '../../../../helpers/removeTrailingSpaces';

export const ANIMATION_DURATION = 350;
export const SEARCH_APP = 'search-app';
const SEARCH_APP_OVERLAY = 'search-app-overlay';
const { isSearchSuggestionsEnabled } = window.bbGlobal.attraqt;

function SearchApp(): React.ReactElement {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const data = useFetch(removeTrailingSpaces(input), open, isSearchSuggestionsEnabled);
  const MAX_INPUT_LENGTH = 40;
  const { innerWidth } = useResizeListener();
  const isSmallOnly = React.useMemo(mediaQuery.isSmallOnly, [innerWidth]);

  const { current: backdropOverlayElement } = useRef(document.getElementById(SEARCH_APP_OVERLAY));

  const inputRef = useRef<HTMLInputElement>(null);

  const transitions = useTransition(open, null, {
    from: { opacity: 0, zIndex: 100 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: ANIMATION_DURATION }
  });

  const canDisplayDropdown = useMemo<boolean>(
    () =>
      isSearchSuggestionsEnabled &&
      open &&
      !!data &&
      data.status !== 'ERROR' &&
      data.successfull === true &&
      (data.data.suggestion?.searchTerms.length > 0 || data.data.suggestion?.products.length > 0),
    [open, data]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInput(e.target.value.substring(0, MAX_INPUT_LENGTH));
  };

  const handleEsc = (e: React.KeyboardEvent): void => {
    if (getPressedKey(e) === KeyboardMap.ESCAPE) {
      e.preventDefault();
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    const insideSearchApp = (e.relatedTarget as HTMLElement)?.closest(`#${SEARCH_APP}`);
    const insideDocument = (e.relatedTarget as HTMLElement)?.closest('html');
    if (!insideSearchApp && insideDocument) {
      setOpen(false);
    }
  };

  const handleOpen = (): void => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus());
  };

  useBodyScrollLock(open);

  return (
    <form
      className="page-header__icons-item page-header__icons-item--search"
      autoComplete="off"
      method="get"
      action="/search"
    >
      <SearchButton handleOpen={handleOpen} />

      {transitions.map(
        ({ item: isOpen, key, props }) =>
          isOpen && (
            <animated.div
              onBlur={handleBlur}
              onKeyDown={handleEsc}
              tabIndex={0}
              key={key}
              style={props}
              className={style['search-app']}
            >
              <div className={style['search-app__header']}>
                <Logo />
                {!isSmallOnly && (
                  <Input
                    ref={inputRef}
                    input={input}
                    handleChange={handleChange}
                    handleClear={(): void => setInput('')}
                    maxLength={MAX_INPUT_LENGTH}
                  />
                )}
                <div className={style['search-app__close-button-container']}>
                  <CloseButton handleClick={(): void => setOpen(false)} />
                </div>
              </div>

              <div className={style['search-app__dropdown']}>
                {isSmallOnly && (
                  <Input
                    ref={inputRef}
                    input={input}
                    handleChange={handleChange}
                    handleClear={(): void => setInput('')}
                    maxLength={MAX_INPUT_LENGTH}
                  />
                )}

                <Dropdown input={input} data={data} canDisplayDropdown={canDisplayDropdown} />
              </div>

              {backdropOverlayElement &&
                createPortal(
                  <BackdropOverlay open={open} handleClose={(): void => setOpen(false)} className="hide-xs-only" />,
                  backdropOverlayElement
                )}
            </animated.div>
          )
      )}
    </form>
  );
}

export default SearchApp;
